import { Breadcrumb, BreadcrumbItem } from '@doda/io-react/pc';
import { BreadcrumbItemTop } from 'components/BreadcrumbItemTop';
import { FC, useMemo, memo } from 'react';
import { JobDetailResponse, JobResponse } from '@doda/api-client/aspida/@types';
import styles from './index.module.scss';
import type { Tab } from '../../types/tab';
import { useTab } from '../TabArea/hooks/useTab';

/**
 * ぱんくずリストコンポーネント
 * @param {JobDetailResponse} job 求人詳細APIレスポンス値
 * @param {Tab} initialTab タブの初期状態
 * @param {jid} string 求人ID
 * @param {JobResponse['wcnsId']} wcncId 原稿種別
 */
interface Props {
  job: JobDetailResponse;
  initialTab: Tab;
  jid: string;
  wcnsId: JobResponse['wcnsId'];
}

export const BreadCrumbList: FC<Props> = memo(
  ({ job, initialTab, jid, wcnsId }) => {
    const { currentTabName } = useTab({
      initialTab,
      jid,
      wcnsId: wcnsId || '',
    });

    const breadCrumb = useMemo(() => {
      const list: { breadCrumbText: string; breadCrumbLink: string }[] = [];

      const industryLName = job.job?.analysis?.industryLName;
      const industryLURL = job.industry?.industryLURL;
      const industrySName = job.job?.analysis?.industrySName;
      const industrySURL = job.industry?.industrySURL;
      const companyId = job.job?.companyId;
      const corporateName = job.job?.corporateName;
      const recuruitPrImage = job.job?.recuruitPrImage?.imageHTML;
      const occupationName = job.job?.occupationName;
      const endText = 'の求人・中途採用情報';

      // 業種大リンクを作成
      if (industryLName && industryLURL) {
        list.push({
          breadCrumbText: industryLName,
          breadCrumbLink: industryLURL,
        });
      }
      // 業種小リンクを作成
      if (industrySName && industrySURL) {
        list.push({
          breadCrumbText: industrySName,
          breadCrumbLink: industrySURL,
        });
      }
      let jdUrl = '';
      if (jid) {
        jdUrl = `/DodaFront/View/JobSearchDetail/j_jid__${jid}/`;
        // E原稿の場合、募集要項へのリンクのURLに付与されるパラメータを追加で付与
        if (recuruitPrImage) {
          jdUrl += '-tab__jd/';
        }
      }
      // 企業TOPページが存在する場合
      if (companyId) {
        if (corporateName) {
          list.push({
            breadCrumbText: `${corporateName}${endText}`,
            breadCrumbLink: `/DodaFront/View/CompanyJobs/j_id__${companyId}/`,
          });
        }
        if (occupationName && jdUrl !== '') {
          list.push({
            breadCrumbText: `${occupationName}${endText}`,
            breadCrumbLink: jdUrl,
          });
        }
      } else if (corporateName && occupationName && jdUrl !== '') {
        list.push({
          breadCrumbText: `${corporateName}／${occupationName}${endText}`,
          breadCrumbLink: jdUrl,
        });
      }
      // アクティブタブがPickUpの場合、最後の要素を追加
      if (currentTabName === 'pr') {
        list.push({
          breadCrumbText: 'Pick up!',
          breadCrumbLink: '',
        });
      }

      return list;
    }, [job, jid, currentTabName]);

    return (
      <div className={styles['jobSearchDetail-breadCrumbList__wrapper']}>
        <Breadcrumb
          className={styles['jobSearchDetail-breadCrumbList__breadClumb']}
        >
          <BreadcrumbItemTop />
          {breadCrumb.map(({ breadCrumbText, breadCrumbLink }, index) => {
            return (
              <BreadcrumbItem
                className={
                  styles['jobSearchDetail-breadCrumbList__breadClumbItem']
                }
                key={index}
                href={
                  index === breadCrumb.length - 1 ? undefined : breadCrumbLink
                }
              >
                {breadCrumbText}
              </BreadcrumbItem>
            );
          })}
        </Breadcrumb>
      </div>
    );
  }
);

BreadCrumbList.displayName = 'BreadCrumbList';
